// What the TimeBlocker screen works out from one day, without saving anything:
// the list's groups, where things sit on the timeline (minutes from the day's
// midnight), how a drag moves a range, how overlapping things share a lane's
// width, and how long one worked.
//
// Promise:
// - The list is derived on every render from the day's answer (open Actions,
//   and the done ones of that day). An open Action is "on the day" when the day
//   is its plan or it has a block that day; otherwise it goes before or after
//   the day by its plan's last day (a week counts as after while the day is in
//   it); without a plan it is listed only when one can start it now (the
//   current step) or it was put off for later. Waitings are never listed.
// - Times are counted in the account's time zone: a day is from its midnight
//   there to the next one.
// - A range dragged on the timeline moves in 15-minute steps, stays within the
//   day, and is never shorter than one step.

import {
  addDays,
  type CaseDayAction,
  planLastDay,
} from "@trameria/core/cases-shared";
import {
  BLOCK_MIN_MINUTES,
  type TimeBlock,
  type TimeBlockerDay,
} from "@trameria/core/timeblocker-shared";
import { zonedParts } from "../time/format";

export const DAY_MINUTES = 24 * 60;
/** One hour's height on the timeline (px). */
export const HOUR_PX = 48;
export const PX_PER_MIN = HOUR_PX / 60;
/** The step every drag and resize snaps to (minutes). */
export const SNAP_MINUTES = BLOCK_MIN_MINUTES;
/** How long a block put from the list is (Actions have no length of their own). */
export const DROP_MINUTES = 30;

export interface DayItem {
  action: CaseDayAction;
  /** The Action's blocks overlapping the day, by start. */
  blocks: TimeBlock[];
}

export interface DaySections {
  /** On the day: planned for it, or with a block on it. */
  todo: DayItem[];
  /** Planned before the day (carried over when it is today). */
  before: DayItem[];
  /** Planned after the day, or for the week the day is in. */
  after: DayItem[];
  /** No plan: the current step of a Case, or put off for later. */
  undated: DayItem[];
  /** Done that day, planned for it and done, or done with a block on it. */
  done: DayItem[];
  /** The blocks to draw, with their Action. */
  placed: { block: TimeBlock }[];
}

const isOnDay = (a: CaseDayAction, day: string) =>
  a.plan?.scope === "day" && a.plan.on === day;

export function daySections(
  data: Pick<TimeBlockerDay, "actions" | "blocks"> | undefined,
  day: string,
): DaySections {
  const blocksOf = new Map<string, TimeBlock[]>();
  for (const b of data?.blocks ?? []) {
    for (const link of b.links) {
      if (!link.stepId) continue;
      const list = blocksOf.get(link.stepId) ?? [];
      list.push(b);
      blocksOf.set(link.stepId, list);
    }
  }
  const items = new Map<string, DayItem>();
  for (const a of data?.actions ?? [])
    if (!items.has(a.id))
      items.set(a.id, {
        action: a,
        blocks: [...(blocksOf.get(a.id) ?? [])].sort(byStart),
      });

  const out: DaySections = {
    todo: [],
    before: [],
    after: [],
    undated: [],
    done: [],
    placed: [],
  };
  for (const item of items.values()) {
    const a = item.action;
    if (a.status === "done") out.done.push(item);
    else if (a.status !== "open") continue;
    else if (item.blocks.length > 0 || isOnDay(a, day)) out.todo.push(item);
    else if (a.plan)
      (planLastDay(a.plan) < day ? out.before : out.after).push(item);
    // An Action after a step still open cannot be started yet: without a
    // plan it waits outside the list.
    else if (a.current || a.someday) out.undated.push(item);
  }
  // Those with a block first, by start; the rest by plan, oldest first.
  const firstStart = (i: DayItem) => i.blocks[0]?.startAt ?? "";
  const planOn = (i: DayItem) => i.action.plan?.on ?? "";
  out.todo.sort((a, b) => {
    const sa = firstStart(a);
    const sb = firstStart(b);
    if (sa && sb) return sa.localeCompare(sb);
    if (sa || sb) return sa ? -1 : 1;
    return planOn(a).localeCompare(planOn(b));
  });
  const byPlan = (a: DayItem, b: DayItem) => planOn(a).localeCompare(planOn(b));
  out.before.sort(byPlan);
  out.after.sort(byPlan);
  // The inbox first, later after.
  out.undated.sort(
    (a, b) => Number(a.action.someday) - Number(b.action.someday),
  );
  out.done.sort((a, b) =>
    (a.action.closedAt ?? "").localeCompare(b.action.closedAt ?? ""),
  );
  for (const block of [...(data?.blocks ?? [])].sort(byStart)) {
    out.placed.push({ block });
  }
  return out;
}

const byStart = (a: TimeBlock, b: TimeBlock) =>
  a.startAt.localeCompare(b.startAt);

// ---- days and minutes ---------------------------------------------------------

/** The instant a day starts in the time zone (ms since the epoch). */
export function dayStartIn(date: string, timeZone?: string): number {
  const [y, mo, d] = date.split("-").map(Number);
  const guess = Date.UTC(y, mo - 1, d, 0, 0, 0);
  const p = zonedParts(new Date(guess), timeZone);
  const asUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return guess - (asUtc - guess);
}

/** The day's range `[from, to)` as ISO instants (what the server reads). */
export function dayRange(
  date: string,
  timeZone?: string,
): { from: string; to: string } {
  return {
    from: new Date(dayStartIn(date, timeZone)).toISOString(),
    to: new Date(dayStartIn(addDays(date, 1), timeZone)).toISOString(),
  };
}

/** Minutes from the day's start (cut to the day: 0 to 1440). */
export function minutesInDay(iso: string, dayStart: number): number {
  const at = (new Date(iso).getTime() - dayStart) / 60_000;
  return Math.min(Math.max(at, 0), DAY_MINUTES);
}

/** Minutes from the day's start, uncut (before the day is negative). */
export const rawMinutes = (iso: string, dayStart: number) =>
  (new Date(iso).getTime() - dayStart) / 60_000;

/** The instant `minutes` after the day's start, as ISO. */
export const isoAt = (dayStart: number, minutes: number) =>
  new Date(dayStart + minutes * 60_000).toISOString();

export const snapMinutes = (minutes: number) =>
  Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;

/** Minutes from midnight as `9:30` (the timeline's own clock). */
export function clock(minutes: number): string {
  const total = Math.round(minutes);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Minutes from midnight as a time field's value (`09:30`). */
export function timeValueOf(minutes: number): string {
  const total = Math.min(Math.max(Math.round(minutes), 0), DAY_MINUTES - 1);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** A time field's value (`09:30`) as minutes from midnight; null when unreadable. */
export function minutesOfValue(value: string): number | null {
  const hit = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!hit) return null;
  const h = Number(hit[1]);
  const mm = Number(hit[2]);
  return h < 24 && mm < 60 ? h * 60 + mm : null;
}

// ---- dragging on the timeline -----------------------------------------------

/** What a drag on the timeline moves: the whole range, its top or its bottom. */
export type DragMode = "move" | "top" | "bottom";

/**
 * The new range after dragging by `delta` minutes (already snapped). Stays
 * within the day; a resize never makes it shorter than one step. Blocks and
 * events move by the same rule.
 */
export function dragRange(
  mode: DragMode,
  start: number,
  end: number,
  delta: number,
): { start: number; end: number } {
  if (mode === "move") {
    const len = end - start;
    const s = Math.min(Math.max(start + delta, 0), DAY_MINUTES - len);
    return { start: s, end: s + len };
  }
  if (mode === "top")
    return {
      start: Math.min(Math.max(start + delta, 0), end - SNAP_MINUTES),
      end,
    };
  return {
    start,
    end: Math.max(Math.min(end + delta, DAY_MINUTES), start + SNAP_MINUTES),
  };
}

/** Where a block put from the list starts: the step under the pointer, kept in the day. */
export function dropStart(minute: number): number {
  const at = Math.floor(minute / SNAP_MINUTES) * SNAP_MINUTES;
  return Math.min(Math.max(at, 0), DAY_MINUTES - DROP_MINUTES);
}

/**
 * Overlapping things side by side: each cluster of overlaps gets as many
 * columns as it needs at most, and each thing its column and the cluster's
 * number of columns.
 */
export function layoutColumns(
  items: readonly { key: string; start: number; end: number }[],
): Map<string, { col: number; cols: number }> {
  const out = new Map<string, { col: number; cols: number }>();
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  let cluster: { key: string; col: number }[] = [];
  let columnsEnd: number[] = [];
  let clusterEnd = Number.NEGATIVE_INFINITY;
  const flush = () => {
    for (const c of cluster)
      out.set(c.key, { col: c.col, cols: columnsEnd.length });
    cluster = [];
    columnsEnd = [];
  };
  for (const it of sorted) {
    if (it.start >= clusterEnd) flush();
    let col = columnsEnd.findIndex((end) => end <= it.start);
    if (col === -1) col = columnsEnd.length;
    columnsEnd[col] = it.end;
    cluster.push({ key: it.key, col });
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  flush();
  return out;
}

// ---- work logs ---------------------------------------------------------------

/** A length (ms) as `h:mm` (`0:23`, `1:05`): minutes rounded down, never below 0. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 60_000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** The title shown everywhere a Block appears (the same in its notice). */
export { blockLabel } from "@trameria/core/timeblocker-shared";
